import { createFileRoute } from '@tanstack/react-router';
import { ChartBar, ChatCircle, Clock, CreditCard, Envelope, Eye, Star } from '@phosphor-icons/react';
import { Icon } from '../../../../../components/Icon';
import { SettingsRow, SettingsSection } from '../../../../../components/settings/settings-section';
import { Card } from '../../../../../components/ui/card';
import { Toggle } from '../../../../../components/ui/toggle';
import { formatClock, useNotificationPrefs, type NotificationPrefs } from '../../../../../lib/notification-prefs';
import { useSession } from '../../../../../lib/session';

export const Route = createFileRoute('/subdomain/app/_client/settings/notifications')({
  component: NotificationsPage,
});

const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

function ToggleRow({
  icon,
  title,
  subtitle,
  checked,
  onChange,
}: {
  icon: Parameters<typeof Icon>[0]['icon'];
  title: string;
  subtitle: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <SettingsRow
      icon={<Icon icon={icon} size="md" />}
      title={title}
      subtitle={subtitle}
      trailing={<Toggle aria-label={title} checked={checked} onCheckedChange={onChange} />}
    />
  );
}

/** A "From" / "Until" row: the value on the right is a native time-of-day picker laid over the row. */
function TimeRow({
  title,
  value,
  onChange,
  disabled,
}: {
  title: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <label className="flex items-center gap-(--mbm-space-4) py-(--mbm-space-3)">
      <span className="flex size-(--mbm-size-icon-tile-large) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-surface-tint text-text-primary">
        <Icon icon={Clock} size="md" />
      </span>
      <span className="flex-1 text-label-large text-text-primary">{title}</span>
      <select
        aria-label={title}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-(--mbm-radius-xs) bg-transparent text-right text-body-medium text-text-secondary outline-none focus-visible:shadow-(--mbm-shadow-focus-ring) disabled:opacity-(--mbm-opacity-disabled)"
      >
        {HOURS.map((hour) => (
          <option key={hour} value={hour}>
            {formatClock(hour)}
          </option>
        ))}
      </select>
    </label>
  );
}

const divide = 'divide-y divide-border-line px-(--mbm-space-4)';

function NotificationsPage() {
  const { prefs, setPush, setEmail, setQuiet } = useNotificationPrefs();
  const { data: session } = useSession();
  const push = (key: keyof NotificationPrefs['push']) => (v: boolean) => setPush(key, v);

  return (
    <>
      <SettingsSection title="App & browser">
        <Card padding="none" className={divide}>
          <ToggleRow
            icon={Eye}
            title="Ready for your review"
            subtitle="New posts, reels and website updates"
            checked={prefs.push.readyForReview}
            onChange={push('readyForReview')}
          />
          <ToggleRow
            icon={ChatCircle}
            title="Chat messages"
            subtitle="Replies from your team"
            checked={prefs.push.chatMessages}
            onChange={push('chatMessages')}
          />
          <ToggleRow
            icon={ChartBar}
            title="Reports"
            subtitle="Weekly health score and monthly report"
            checked={prefs.push.reports}
            onChange={push('reports')}
          />
          <ToggleRow
            icon={CreditCard}
            title="Payments"
            subtitle="Invoices and payment reminders"
            checked={prefs.push.payments}
            onChange={push('payments')}
          />
          <ToggleRow
            icon={Star}
            title="New reviews"
            subtitle="When customers review you on Google"
            checked={prefs.push.newReviews}
            onChange={push('newReviews')}
          />
        </Card>
      </SettingsSection>

      <SettingsSection title="Email">
        <Card padding="none" className={divide}>
          <ToggleRow
            icon={Envelope}
            title="Monthly report"
            subtitle={`PDF to ${session?.user.email ?? 'your email'}`}
            checked={prefs.email.monthlyReport}
            onChange={(v) => setEmail('monthlyReport', v)}
          />
          <ToggleRow
            icon={Envelope}
            title="Product updates"
            subtitle="New features, once a month"
            checked={prefs.email.productUpdates}
            onChange={(v) => setEmail('productUpdates', v)}
          />
        </Card>
      </SettingsSection>

      <SettingsSection title="Quiet hours">
        <Card padding="none" className={divide}>
          <ToggleRow
            icon={Clock}
            title="Pause at night"
            subtitle={`No pushes from ${formatClock(prefs.quietHours.from)} to ${formatClock(prefs.quietHours.until)}`}
            checked={prefs.quietHours.pauseAtNight}
            onChange={(v) => setQuiet({ pauseAtNight: v })}
          />
          <TimeRow
            title="From"
            value={prefs.quietHours.from}
            disabled={!prefs.quietHours.pauseAtNight}
            onChange={(from) => setQuiet({ from })}
          />
          <TimeRow
            title="Until"
            value={prefs.quietHours.until}
            disabled={!prefs.quietHours.pauseAtNight}
            onChange={(until) => setQuiet({ until })}
          />
        </Card>
      </SettingsSection>
    </>
  );
}
